import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useAuth } from '../../context/AuthContext';
import Button from '../common/Button';
import { Input } from '../common/Input';
import { FaEnvelope, FaLock, FaUser, FaBuilding, FaMapMarkerAlt, FaSpinner } from 'react-icons/fa';

const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
  width: 100%;
  max-width: 400px;
  margin: 0 auto;
  padding-bottom: 1rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-size: 0.9rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text};
`;

const InputWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const IconWrapper = styled.div`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: ${({ theme }) => theme.colors.primary};
  font-size: 1rem;
  
  .fa-spin {
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const StyledInput = styled(Input)`
  padding-left: 2.5rem;
  width: 100%;
`;

const ErrorMessage = styled.div`
  color: ${({ theme }) => theme.colors.error};
  font-size: 0.85rem;
  margin-top: 0.25rem;
`;

const AddressSuggestionsList = styled.ul`
  position: absolute;
  width: 100%;
  max-height: 200px;
  overflow-y: auto;
  background-color: white;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  z-index: 10;
  margin: 0;
  padding: 0;
  list-style: none;
`;

const AddressSuggestionItem = styled.li`
  padding: 10px 15px;
  cursor: pointer;
  &:hover {
    background-color: ${({ theme }) => theme.colors.background};
  }
`;

const SuccessMessage = styled.div`
  color: ${({ theme }) => theme.colors.success};
  font-size: 0.9rem;
  margin-top: 1rem;
  padding: 0.75rem;
  background-color: ${({ theme }) => `${theme.colors.success}10`};
  border-radius: 4px;
  text-align: center;
`;

const SectionTitle = styled.h3`
  font-size: 1.1rem;
  color: ${({ theme }) => theme.colors.primary};
  margin-top: 0.5rem;
  margin-bottom: 0.3rem;
  padding-bottom: 0.3rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;


const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
`;

interface RegisterFormProps {
  onSwitchToLogin: () => void;
}

// Interface pour les suggestions d'adresses
interface AddressSuggestion {
  display_name: string;
  lat: string;
  lon: string;
  place_id?: string; // ID unique de la suggestion (utilisé par Mapbox)
}

const RegisterForm: React.FC<RegisterFormProps> = ({ onSwitchToLogin }) => {
  const { signUp, error, loading, clearError } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [agencyName, setAgencyName] = useState('');
  const [agencyAddress, setAgencyAddress] = useState('');
  // Photo sera toujours null - fonctionnalité supprimée
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  
  // États pour l'autocomplétion d'adresse
  const [addressSuggestions, setAddressSuggestions] = useState<AddressSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isSearchingAddress, setIsSearchingAddress] = useState(false);
  
  const [validationErrors, setValidationErrors] = useState<{ 
    email?: string; 
    password?: string;
    confirmPassword?: string;
    name?: string;
    firstName?: string;
    agencyName?: string;
    agencyAddress?: string;
  }>({});
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  
  // Gérer le changement d'adresse
  const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setAgencyAddress(value);
    
    // Si moins de 3 caractères, ne pas chercher
    if (!value || value.length < 3) {
      setAddressSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    
    // Rechercher des adresses après une courte période
    setIsSearchingAddress(true);
    
    // Utiliser setTimeout au lieu de debounce
    setTimeout(async () => {
      try {
        // Utiliser l'API Mapbox Geocoding au lieu de Nominatim
        const mapboxToken = process.env.REACT_APP_MAPBOX_TOKEN;
        if (!mapboxToken) {
          throw new Error('Token Mapbox non défini');
        }
        
        const response = await fetch(
          `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(value)}.json?access_token=${mapboxToken}&country=fr&language=fr&limit=5&types=address,place`,
          {
            headers: {
              'Accept-Language': 'fr'
            }
          }
        );
        
        if (response.ok) {
          const data = await response.json();
          // Transformer les données Mapbox au format attendu par le composant
          const suggestions = data.features.map((feature: any) => ({
            display_name: feature.place_name,
            place_id: feature.id,
            lat: feature.center[1],
            lon: feature.center[0]
          }));
          setAddressSuggestions(suggestions);
          setShowSuggestions(true);
        } else {
          console.error('Erreur lors de la recherche d\'adresses:', response.statusText);
          setAddressSuggestions([]);
        }
      } catch (error) {
        console.error('Exception lors de la recherche d\'adresses:', error);
        setAddressSuggestions([]);
      } finally {
        setIsSearchingAddress(false);
      }
    }, 300); // Délai réduit à 300ms pour une meilleure réactivité
  };
  
  // Gérer les clics sur le document pour fermer les suggestions
  useEffect(() => {
    const handleDocumentClick = () => {
      // Simple gestionnaire de clic global pour fermer les suggestions
      // Nous utilisons setTimeout pour permettre au clic de sélection de se produire d'abord
      setTimeout(() => {
        setShowSuggestions(false);
      }, 200);
    };
    
    document.addEventListener('click', handleDocumentClick);
    return () => {
      document.removeEventListener('click', handleDocumentClick);
    };
  }, []);
  
  // Sélectionner une adresse suggérée
  const selectAddress = (suggestion: AddressSuggestion) => {
    // Nettoyer l'adresse pour éviter les caractères indésirables
    const cleanAddress = suggestion.display_name
      .replace(/^[^a-zA-Z0-9\u00C0-\u00FF]+/, '') // Supprimer les caractères non alphanumériques au début (compatible ES5)
      .replace(/,\s*France$/, '') // Supprimer ", France" à la fin si présent
      .trim();
    
    setAgencyAddress(cleanAddress);
    setShowSuggestions(false);
    setAddressSuggestions([]); // Vider les suggestions après sélection
    
    // Déplacer le focus pour éviter que les suggestions réapparaissent
    setTimeout(() => {
      // Utiliser document.activeElement?.blur() au lieu de inputRef.current.blur()
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
    }, 100);
  };

  const validateForm = (): boolean => {
    const errors: { 
      email?: string; 
      password?: string;
      confirmPassword?: string;
      name?: string;
      firstName?: string;
      agencyName?: string;
      agencyAddress?: string;
    } = {};
    
    if (!email.trim()) {
      errors.email = 'L\'email est requis';
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      errors.email = 'L\'email est invalide';
    }
    
    if (!password) {
      errors.password = 'Le mot de passe est requis';
    } else if (password.length < 6) {
      errors.password = 'Le mot de passe doit contenir au moins 6 caractères';
    }
    
    if (!confirmPassword) {
      errors.confirmPassword = 'Veuillez confirmer votre mot de passe';
    } else if (confirmPassword !== password) {
      errors.confirmPassword = 'Les mots de passe ne correspondent pas';
    }

    if (!name.trim()) {
      errors.name = 'Le nom est requis';
    }

    if (!firstName.trim()) {
      errors.firstName = 'Le prénom est requis';
    }

    if (!agencyName.trim()) {
      errors.agencyName = 'Le nom de l\'agence est requis';
    }

    if (!agencyAddress.trim()) {
      errors.agencyAddress = 'L\'adresse de l\'agence est requise';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Anti double-clic : si déjà en chargement, ne rien faire
    if (loading) return;
    
    clearError();
    setRegistrationSuccess(false);
    
    if (validateForm()) {
      try {
        // Géocoder l'adresse de l'agence AVANT l'inscription
        let agencyCoordinates: [number, number] | null = null;
        
        if (agencyAddress) {
          try {
            const mapboxToken = process.env.REACT_APP_MAPBOX_TOKEN;
            if (mapboxToken) {
              const response = await fetch(
                `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(agencyAddress)}.json?access_token=${mapboxToken}&country=fr&language=fr&limit=1`,
                { headers: { 'Accept-Language': 'fr' } }
              );
              
              if (response.ok) {
                const data = await response.json();
                if (data.features && data.features.length > 0) {
                  agencyCoordinates = data.features[0].center; // [longitude, latitude]
                  console.log('✅ Adresse géocodée:', agencyCoordinates);
                }
              }
            }
          } catch (geoError) {
            console.warn('Erreur de géocodage (non bloquant):', geoError);
            // On continue l'inscription même si le géocodage échoue
          }
        }
        
        // Stocker les coordonnées dans sessionStorage pour les utiliser après l'inscription
        if (agencyCoordinates) {
          sessionStorage.setItem('pendingAgencyMarker', JSON.stringify({
            address: agencyAddress,
            coordinates: agencyCoordinates,
            agencyName: agencyName
          }));
        }
        
        await signUp({
          email,
          password,
          name,
          firstName,
          agencyName,
          agencyAddress,
          photoFile
        });
        setRegistrationSuccess(true);
        setEmail('');
        setPassword('');
        setConfirmPassword('');
        setName('');
        setFirstName('');
        setAgencyName('');
        setAgencyAddress('');
        setPhotoFile(null);
      } catch (err) {
        // L'erreur est déjà gérée par le contexte d'authentification
      }
    }
  };

  return (
    <FormContainer onSubmit={handleSubmit}>
      <h2>Inscription</h2>
      
      <SectionTitle>Informations personnelles</SectionTitle>
      
      <FormGroup>
        <Label htmlFor="name">Nom</Label>
        <InputWrapper>
          <IconWrapper>
            <FaUser />
          </IconWrapper>
          <StyledInput
            id="name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Votre nom"
            $hasError={!!validationErrors.name}
          />
        </InputWrapper>
        {validationErrors.name && <ErrorMessage>{validationErrors.name}</ErrorMessage>}
      </FormGroup>

      <FormGroup>
        <Label htmlFor="firstName">Prénom</Label>
        <InputWrapper>
          <IconWrapper>
            <FaUser />
          </IconWrapper>
          <StyledInput
            id="firstName"
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder="Votre prénom"
            $hasError={!!validationErrors.firstName}
          />
        </InputWrapper>
        {validationErrors.firstName && <ErrorMessage>{validationErrors.firstName}</ErrorMessage>}
      </FormGroup>
      
      <FormGroup>
        <Label htmlFor="email">Email</Label>
        <InputWrapper>
          <IconWrapper>
            <FaEnvelope />
          </IconWrapper>
          <StyledInput
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Votre adresse email"
            $hasError={!!validationErrors.email}
          />
        </InputWrapper>
        {validationErrors.email && <ErrorMessage>{validationErrors.email}</ErrorMessage>}
      </FormGroup>

      
      <SectionTitle>Informations de l'agence</SectionTitle>
      
      <FormGroup>
        <Label htmlFor="agencyName">Nom de l'agence</Label>
        <InputWrapper>
          <IconWrapper>
            <FaBuilding />
          </IconWrapper>
          <StyledInput
            id="agencyName"
            type="text"
            value={agencyName}
            onChange={(e) => setAgencyName(e.target.value)}
            placeholder="Nom de votre agence"
            $hasError={!!validationErrors.agencyName}
          />
        </InputWrapper>
        {validationErrors.agencyName && <ErrorMessage>{validationErrors.agencyName}</ErrorMessage>}
      </FormGroup>

      <FormGroup>
        <Label htmlFor="agencyAddress">Adresse de l'agence</Label>
        <InputWrapper>
          <IconWrapper>
            {isSearchingAddress ? <FaSpinner className="fa-spin" /> : <FaMapMarkerAlt />}
          </IconWrapper>
          <StyledInput
            id="agencyAddress"
            type="text"
            value={agencyAddress}
            onChange={handleAddressChange}
            placeholder="Commencez à taper l'adresse..."
            $hasError={!!validationErrors.agencyAddress}
            autoComplete="off"
          />
        </InputWrapper>
        {showSuggestions && addressSuggestions.length > 0 && (
          <AddressSuggestionsList>
            {addressSuggestions.map((suggestion, index) => {
              // Nettoyer l'adresse pour l'affichage
              const displayAddress = suggestion.display_name
                .replace(/^[^a-zA-Z0-9\u00C0-\u00FF]+/, '') // Supprimer les caractères non alphanumériques au début
                .replace(/,\s*France$/, '') // Supprimer ", France" à la fin si présent
                .trim();
                
              return (
                <AddressSuggestionItem 
                  key={suggestion.place_id || index} 
                  onClick={() => selectAddress(suggestion)}
                >
                  {displayAddress}
                </AddressSuggestionItem>
              );
            })}
          </AddressSuggestionsList>
        )}
        {validationErrors.agencyAddress && <ErrorMessage>{validationErrors.agencyAddress}</ErrorMessage>}
      </FormGroup>
      
      <SectionTitle>Sécurité</SectionTitle>
      
      <FormGroup>
        <Label htmlFor="password">Mot de passe</Label>
        <InputWrapper>
          <IconWrapper>
            <FaLock />
          </IconWrapper>
          <StyledInput
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Votre mot de passe"
            $hasError={!!validationErrors.password}
          />
        </InputWrapper>
        {validationErrors.password && <ErrorMessage>{validationErrors.password}</ErrorMessage>}
      </FormGroup>
      
      <FormGroup>
        <Label htmlFor="confirmPassword">Confirmer le mot de passe</Label>
        <InputWrapper>
          <IconWrapper>
            <FaLock />
          </IconWrapper>
          <StyledInput
            id="confirmPassword"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Confirmez votre mot de passe"
            $hasError={!!validationErrors.confirmPassword}
          />
        </InputWrapper>
        {validationErrors.confirmPassword && <ErrorMessage>{validationErrors.confirmPassword}</ErrorMessage>}
      </FormGroup>
      
      {error && <ErrorMessage>{error.message}</ErrorMessage>}
      
      {registrationSuccess && (
        <SuccessMessage>
          Inscription réussie ! Veuillez vérifier votre email pour confirmer votre compte.
        </SuccessMessage>
      )}
      
      <ButtonsContainer>
        <Button type="submit" $variant="primary" disabled={loading}>
          {loading ? 'Création en cours...' : 'S\'inscrire'}
        </Button>
        <Button type="button" $variant="text" onClick={onSwitchToLogin}>
          Déjà un compte ? Se connecter
        </Button>
      </ButtonsContainer>
    </FormContainer>
  );
};

export default RegisterForm;

